import { useState, useCallback, useEffect, useRef } from 'react';
import { Edit2, Trash2, ChevronRight, Eye, FileSearch, Search, X } from 'lucide-react';
import { Dropdown } from '../ui/Dropdown';
import { Pagination } from '../ui/Pagination';
import type { Account } from '../../types/accounting';

interface AccountListProps {
  accounts: Account[];
  onEdit: (account: Account) => void;
  onDelete: ((account: Account) => void) | undefined;
  onSelect?: (account: Account) => void;
  selectedAccount?: Account | null;
  loading?: boolean;
  /** Pagination depuis le backend */
  pagination?: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
  /** Callback pour changement de page (si pagination backend) */
  onPageChange?: (page: number) => void;
  /** Callback pour recherche */
  onSearch?: (search: string) => void;
}

const accountTypeConfig = {
  asset: { label: 'Actif', color: 'text-blue-600 dark:text-blue-400', bgColor: 'bg-blue-50 dark:bg-blue-900/20' },
  liability: { label: 'Passif', color: 'text-red-600 dark:text-red-400', bgColor: 'bg-red-50 dark:bg-red-900/20' },
  equity: { label: 'Capitaux', color: 'text-purple-600 dark:text-purple-400', bgColor: 'bg-purple-50 dark:bg-purple-900/20' },
  revenue: { label: 'Produits', color: 'text-green-600 dark:text-green-400', bgColor: 'bg-green-50 dark:bg-green-900/20' },
  expense: { label: 'Charges', color: 'text-orange-600 dark:text-orange-400', bgColor: 'bg-orange-50 dark:bg-orange-900/20' }
};

export function AccountList({ 
  accounts, 
  onEdit, 
  onDelete, 
  onSelect,
  selectedAccount,
  loading,
  pagination,
  onPageChange,
  onSearch
}: AccountListProps) {
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const lastSearchRef = useRef('');
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  
  // Protection pour les données undefined/null
  const safeAccounts = Array.isArray(accounts) ? accounts.filter(account => account != null && account.code && account.name) : [];

  // Debounce pour la recherche - avec ref pour éviter les appels dupliqués
  useEffect(() => {
    if (!onSearch) return;
    
    // Annuler le timer précédent
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    // Ne pas appeler si la valeur n'a pas changé
    if (searchTerm === lastSearchRef.current) return;
    
    debounceTimerRef.current = setTimeout(() => {
      lastSearchRef.current = searchTerm;
      onSearch(searchTerm);
    }, 500); // Augmenté à 500ms pour réduire les appels
    
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchTerm, onSearch]);

  // Réinitialiser la recherche
  const handleClearSearch = useCallback(() => {
    setSearchTerm('');
    lastSearchRef.current = '';
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    onSearch?.('');
  }, [onSearch]);

  if (loading) {
    return (
      <div className="space-y-2 p-2">
        {[...Array(8)].map((_, i) => (
          <div key={i} className="animate-pulse">
            <div className="h-16 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Barre de recherche */}
      {onSearch && (
        <div className="flex-shrink-0 p-2 border-b border-gray-100 dark:border-gray-800">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher un compte..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full h-9 pl-9 pr-8 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors"
            />
            {searchTerm && (
              <button
                onClick={handleClearSearch}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
              >
                <X className="h-3.5 w-3.5 text-gray-400" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Liste des comptes - hauteur contrainte avec scroll */}
      <div className="flex-1 overflow-y-auto space-y-1 p-1 min-h-0 max-h-[calc(100vh-320px)] scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent">
        {safeAccounts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="p-4 rounded-full bg-gray-100 dark:bg-gray-800 mb-4">
              <FileSearch className="h-8 w-8 text-gray-400" />
            </div>
            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Aucun compte trouvé</p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
              Modifiez vos critères de recherche
            </p>
          </div>
        ) : (
          safeAccounts.map((account) => {
            const isSelected = selectedAccount?.id === account.id;
            const isHovered = hoveredId === account.id;
            const typeConfig = accountTypeConfig[account.type];

            return (
              <div
                key={account.id}
                onClick={() => onSelect?.(account)}
                onMouseEnter={() => setHoveredId(account.id)}
                onMouseLeave={() => setHoveredId(null)}
                className={`
                  group relative p-3 rounded-lg cursor-pointer
                  transition-all duration-200 ease-out
                  ${isSelected 
                    ? 'bg-primary/10 border-l-4 border-primary shadow-sm' 
                    : 'bg-white dark:bg-gray-800 border-l-4 border-transparent hover:border-gray-300 dark:hover:border-gray-600'
                  }
                  ${isHovered && !isSelected ? 'shadow-md transform scale-[1.01]' : 'shadow-sm'}
                `}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <code className={`
                        text-xs font-mono font-semibold px-2 py-0.5 rounded
                        ${isSelected ? 'bg-primary/20 text-primary' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'}
                      `}>
                        {account.code}
                      </code>
                      {typeConfig && (
                        <span className={`
                          text-xs px-2 py-0.5 rounded-full font-medium
                          ${typeConfig.color} ${typeConfig.bgColor}
                        `}>
                          {typeConfig.label}
                        </span>
                      )}
                    </div>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                      {account.name}
                    </p>
                  </div>

                  {/* Actions avec dropdown */}
                  <div className={`
                    flex items-center gap-1 ml-2
                    transition-opacity duration-200
                    ${isHovered || isSelected ? 'opacity-100' : 'opacity-0'}
                  `}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Dropdown
                      items={[
                        {
                          label: 'Voir',
                          icon: Eye,
                          onClick: () => onSelect?.(account),
                          variant: 'primary'
                        },
                        {
                          label: 'Modifier',
                          icon: Edit2,
                          onClick: () => onEdit(account),
                          separator: onDelete ? true : false
                        },
                        ...(onDelete ? [{
                          label: 'Supprimer',
                          icon: Trash2,
                          onClick: () => onDelete(account),
                          variant: 'danger' as const
                        }] : [])
                      ]}
                      align="right"
                    />
                  </div>

                  {/* Indicateur de sélection */}
                  <ChevronRight className={`
                    h-4 w-4 ml-2 transition-all duration-200
                    ${isSelected 
                      ? 'text-primary opacity-100 transform translate-x-0' 
                      : 'text-gray-400 opacity-0 transform -translate-x-2'
                    }
                  `} />
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Footer avec pagination */}
      {safeAccounts.length > 0 && (
        <div className="flex-shrink-0 pt-3 mt-2 border-t border-gray-100 dark:border-gray-800">
          {/* Pagination si fournie */}
          {pagination && pagination.totalPages > 1 && onPageChange && (
            <Pagination
              currentPage={pagination.page}
              totalPages={pagination.totalPages}
              totalItems={pagination.total}
              pageSize={pagination.pageSize}
              onPageChange={onPageChange}
              compact
            />
          )}
          
          {/* Compteur discret */}
          <div className="text-xs text-gray-400 dark:text-gray-500 text-center mt-2">
            {pagination ? (
              <>
                Page {pagination.page} / {pagination.totalPages}
                {' • '}
                {pagination.total} compte{pagination.total > 1 ? 's' : ''} au total
              </>
            ) : (
              <>
                {safeAccounts.length} compte{safeAccounts.length > 1 ? 's' : ''}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}